/**
 * 能力残回数のドットインジケーター表示ユーティリティ
 *
 * 特殊攻撃: ●●●●●○○ （塗りつぶし/空のドット）
 * リフレクター: ■□□ （塗りつぶし/空の盾マーク）
 */

/** インジケーター用の文字定数 */
export const INDICATOR_CHARS = {
  /** 特殊攻撃: 残りあり */
  specialFilled: '●',
  /** 特殊攻撃: 使用済み */
  specialEmpty: '○',
  /** リフレクター: 残りあり */
  reflectorFilled: '■',
  /** リフレクター: 使用済み */
  reflectorEmpty: '□',
} as const;

/** インジケーターの色定数 */
export const INDICATOR_COLORS = {
  /** 特殊攻撃: 通常（残り半分以上） */
  specialNormal: '#cc88ff',
  /** 特殊攻撃: 警告（残り半分未満） */
  specialWarning: '#ffaa44',
  /** 特殊攻撃: 超過（残り0、威力半減状態） */
  specialExceeded: '#ff4444',
  /** リフレクター: 通常（残りあり） */
  reflectorNormal: '#88bbff',
  /** リフレクター: 切れ（残り0、反射ダメージなし） */
  reflectorExhausted: '#ff4444',
} as const;

/**
 * 特殊攻撃の残回数をドットインジケーター文字列に変換
 * @param remaining 残り回数（0以下は全て空ドットとして扱う）
 * @param max 最大回数
 */
export function formatSpecialIndicator(remaining: number, max: number): string {
  const clamped = Math.max(0, remaining);
  const filled = INDICATOR_CHARS.specialFilled.repeat(clamped);
  const empty = INDICATOR_CHARS.specialEmpty.repeat(max - clamped);
  return filled + empty;
}

/**
 * リフレクターの残回数をドットインジケーター文字列に変換
 * @param remaining 残り回数
 * @param max 最大回数
 */
export function formatReflectorIndicator(remaining: number, max: number): string {
  const clamped = Math.max(0, remaining);
  const filled = INDICATOR_CHARS.reflectorFilled.repeat(clamped);
  const empty = INDICATOR_CHARS.reflectorEmpty.repeat(max - clamped);
  return filled + empty;
}

/**
 * 特殊攻撃の残回数に応じたインジケーター色を返す
 * - 半分以上: 通常色（紫系）
 * - 半分未満: 警告色（オレンジ系）
 * - 0以下: 超過色（赤系 = 威力半減）
 */
export function getSpecialIndicatorColor(remaining: number, max: number): string {
  if (remaining <= 0) {
    return INDICATOR_COLORS.specialExceeded;
  }
  if (remaining < Math.ceil(max / 2)) {
    return INDICATOR_COLORS.specialWarning;
  }
  return INDICATOR_COLORS.specialNormal;
}

/**
 * リフレクターの残回数に応じたインジケーター色を返す
 * - 1以上: 通常色（青系）
 * - 0: 切れ色（赤系 = 反射ダメージなし）
 */
export function getReflectorIndicatorColor(remaining: number, _max: number): string {
  if (remaining <= 0) {
    return INDICATOR_COLORS.reflectorExhausted;
  }
  return INDICATOR_COLORS.reflectorNormal;
}
